/**
 * ArchSim Simulation Engine — Mathematical Upgrade & Consistency Fix
 *
 * Models used:
 *  - M/M/c queueing theory for latency, CPU load, and queue depth
 *  - Two-pass traffic propagation (topological order → forward pass → metric pass)
 *  - Little's Law (L = λW) for queue depth
 *  - Zipf/exponential approximation for cache hit rate from working set
 *  - Series/parallel availability for system availability
 *  - Component-specific formulas per node type
 */

import type { CanvasNode, CanvasEdge, ComponentDef } from './archsim-data';
import { ALL_COMPONENTS } from './archsim-data';

// ─── NodeConfig type ──────────────────────────────────────────────────────────

export interface NodeConfig {
  // General
  scale?: number;               // throughput multiplier (legacy)
  replicas?: number;            // horizontal replica count (legacy)
  latencyOverride?: number;     // override base latency (ms)
  failureRate?: number;         // override base error rate (%)
  clientRps?: number;           // per-client RPS (for Client nodes)

  // Server / Microservice
  serviceRatePerInstance?: number; // μ per instance (req/s), default 200
  instances?: number;              // number of server instances (c), default 2

  // Cache
  cacheSizeGB?: number;         // cache capacity in GB, default 4
  workingSetSizeGB?: number;    // working set size in GB, default 20
  alpha?: number;               // Zipf popularity skew exponent, default 1.0

  // Rate Limiter / Gateway
  rateLimit?: number;           // max allowed RPS
  burstLimit?: number;          // burst allowance (token bucket)

  // Timeout
  timeoutMs?: number;           // timeout threshold for latency-based errors, default 1000

  // Message Queue
  consumerCapacity?: number;    // max consumer throughput (req/s)
  partitions?: number;          // number of partitions/topics, default 8

  // Circuit Breaker
  circuitState?: 'closed' | 'open' | 'half-open'; // default 'closed'

  // CDN
  cacheRatio?: number;          // fraction of traffic offloaded (0–100), default 85

  // Database
  instanceSize?: 'small' | 'medium' | 'large' | 'xlarge'; // default 'medium'
  readReplicas?: number;        // number of read replicas
  shards?: number;              // number of shards

  [key: string]: string | number | boolean | undefined;
}

// ─── Chaos event types ────────────────────────────────────────────────────────

export type ChaosEventId =
  | 'chaos-kill-random'
  | 'chaos-latency-spike' |'chaos-packet-loss' |'chaos-cpu-throttle' |'chaos-db-slow' |'chaos-network-partition' |'chaos-memory-leak' |'chaos-cascade';

export interface ChaosState {
  activeEvents: Set<ChaosEventId>;
  eventStartTick: Map<ChaosEventId, number>;
  killedNodeId: string | null;
  partitionGroupA: Set<string>;
}

export function createInitialChaosState(): ChaosState {
  return {
    activeEvents: new Set(),
    eventStartTick: new Map(),
    killedNodeId: null,
    partitionGroupA: new Set(),
  };
}

// ─── Chaos event durations (ticks at 800ms each) ─────────────────────────────
const CHAOS_DURATIONS: Record<ChaosEventId, number> = {
  'chaos-kill-random': 15,
  'chaos-latency-spike': 37,
  'chaos-packet-loss': 25,
  'chaos-cpu-throttle': 50,
  'chaos-db-slow': 19,
  'chaos-network-partition': 30,
  'chaos-memory-leak': 60,
  'chaos-cascade': 20,
};

// ─── M/M/c queueing helpers ───────────────────────────────────────────────────

function factorial(n: number): number {
  if (n <= 1) return 1;
  let r = 1;
  for (let i = 2; i <= n; i++) r *= i;
  return r;
}

/**
 * Erlang-C: probability a request must wait in an M/M/c queue.
 * c = servers, totalRho = λ/μ (total offered load)
 */
function erlangC(c: number, totalRho: number): number {
  const rhoPerServer = totalRho / c;
  if (rhoPerServer >= 1) return 1;

  let sum = 0;
  for (let k = 0; k < c; k++) {
    sum += Math.pow(totalRho, k) / factorial(k);
  }
  const lastTerm = (Math.pow(totalRho, c) / factorial(c)) * (1 / (1 - rhoPerServer));
  const p0 = 1 / (sum + lastTerm);
  const ec = (Math.pow(totalRho, c) / factorial(c)) * (1 / (1 - rhoPerServer)) * p0;
  return Math.min(1, Math.max(0, ec));
}

/**
 * M/M/c mean queueing delay Wq (seconds).
 * arrivalRate = λ, serviceRate = μ per server, c = number of servers
 */
function mmcQueueDelay(arrivalRate: number, serviceRate: number, c: number): number {
  if (serviceRate <= 0) return Infinity;
  const totalRho = arrivalRate / serviceRate; // λ/μ
  const rhoPerServer = totalRho / c;
  if (rhoPerServer >= 0.9999) return Infinity;

  const ec = erlangC(c, totalRho);
  // Wq = C(c,ρ) / (c·μ·(1 - ρ/c))
  return ec / (c * serviceRate * (1 - rhoPerServer));
}

/**
 * M/M/c mean sojourn time W = Wq + 1/μ (seconds).
 */
function mmcSojournTime(arrivalRate: number, serviceRate: number, c: number): number {
  const wq = mmcQueueDelay(arrivalRate, serviceRate, c);
  if (!isFinite(wq)) return Infinity;
  return wq + 1 / serviceRate;
}

// ─── Availability helpers ─────────────────────────────────────────────────────

function componentAvailability(defId: string): number {
  const availabilityMap: Record<string, number> = {
    'load-balancer': 0.9999,
    'api-gateway': 0.9995,
    'cdn': 0.99999,
    'redis': 0.9999,
    'memcached': 0.9999,
    'postgres': 0.9995,
    'mysql': 0.9995,
    'mongodb': 0.9993,
    'cassandra': 0.9998,
    'dynamodb': 0.99999,
    'db-replica': 0.9995,
    'kafka': 0.9998,
    'rabbitmq': 0.9995,
    'sqs': 0.99999,
    'circuit-breaker': 0.99999,
    'web-server': 0.999,
    'app-server': 0.999,
    'microservice': 0.9993,
    'serverless': 0.9999,
    'k8s-pod': 0.9995,
    'worker': 0.998,
  };
  return availabilityMap[defId] ?? 0.999;
}

export function computeSystemAvailability(nodes: CanvasNode[], edges: CanvasEdge[]): number {
  if (nodes.length === 0) return 1;

  const bandWidth = 120;
  const bands = new Map<number, CanvasNode[]>();
  for (const node of nodes) {
    const band = Math.round(node.x / bandWidth);
    if (!bands.has(band)) bands.set(band, []);
    bands.get(band)!.push(node);
  }

  const sortedBands = Array.from(bands.entries()).sort((a, b) => a[0] - b[0]);
  let systemAvail = 1;
  for (const [, layerNodes] of sortedBands) {
    const failProb = layerNodes.reduce((prod, n) => {
      return prod * (1 - componentAvailability(n.defId));
    }, 1);
    systemAvail *= (1 - failProb);
  }
  return Math.min(1, Math.max(0, systemAvail));
}

// ─── Cache hit rate (exponential approximation of Zipf) ──────────────────────

/**
 * Cache hit rate using exponential approximation:
 * hitRate = min(95, max(5, 100 × (1 - exp(-alpha × cacheSizeGB / workingSetSizeGB))))
 */
export function cacheHitRateFromWorkingSet(
  cacheSizeGB: number,
  workingSetSizeGB: number,
  alpha: number = 1.0
): number {
  if (cacheSizeGB <= 0 || workingSetSizeGB <= 0) return 0;
  if (cacheSizeGB >= workingSetSizeGB) return 95;
  const raw = 100 * (1 - Math.exp(-alpha * cacheSizeGB / workingSetSizeGB));
  return Math.min(95, Math.max(5, raw));
}

/**
 * Legacy Zipf cache hit rate (kept for backward compatibility / test suite).
 */
export function zipfCacheHitRate(
  cacheCapacity: number,
  workingSetSize: number,
  zipfExponent: number = 0.9
): number {
  if (cacheCapacity >= workingSetSize) return 1;
  if (cacheCapacity <= 0) return 0;

  const harmonicPartial = (n: number, s: number): number => {
    if (n <= 0) return 0;
    if (s === 1) return Math.log(n) + 0.5772156649;
    return (Math.pow(n, 1 - s) - 1) / (1 - s) + 1;
  };

  const numerator = harmonicPartial(cacheCapacity, zipfExponent);
  const denominator = harmonicPartial(workingSetSize, zipfExponent);
  return Math.min(1, Math.max(0, numerator / denominator));
}

// ─── P99 latency ─────────────────────────────────────────────────────────────

/**
 * P99 ≈ 3 × mean latency (spec approximation).
 */
export function p99FromMeanLatency(meanLatencyMs: number, _cv: number = 1.5): number {
  if (meanLatencyMs <= 0) return 0;
  if (!isFinite(meanLatencyMs)) return 9999;
  return meanLatencyMs * 3;
}

// ─── Little's Law queue depth ─────────────────────────────────────────────────

export function littlesLawQueueDepth(arrivalRate: number, meanSojournTimeSec: number): number {
  return arrivalRate * meanSojournTimeSec;
}

// ─── Per-node simulation result ───────────────────────────────────────────────

export interface NodeSimResult {
  rps: number;
  outgoingRps: number;   // RPS propagated downstream (may differ for filtering nodes)
  latency: number;       // mean latency ms
  p99Latency: number;    // P99 latency ms
  errorRate: number;     // %
  cpuLoad: number;       // %
  memoryLoad: number;    // %
  queueDepth: number;    // items in queue
  status: CanvasNode['status'];
}

// ─── Source node IDs ──────────────────────────────────────────────────────────

const SOURCE_DEF_IDS = new Set([
  'web-browser', 'mobile-app', 'iot-device', 'bot', 'cli-client',
  'desktop-app', 'third-party', 'partner-api',
]);

function isSourceNode(defId: string): boolean {
  return SOURCE_DEF_IDS.has(defId);
}

function getNodeGroup(defId: string): string {
  return ALL_COMPONENTS.find(c => c.id === defId)?.group ?? '';
}

// ─── Component-specific node metric computation ───────────────────────────────

/**
 * Compute per-node metrics using component-specific M/M/c formulas.
 * Returns both processed RPS and outgoing RPS (which may be filtered).
 */
export function computeNodeRuntime(
  defId: string,
  def: ComponentDef,
  lambda: number,          // incoming RPS
  config: NodeConfig,
  chaosState: ChaosState,
  nodeId: string,
  tick: number
): NodeSimResult {
  const events = chaosState.activeEvents;

  // ── Chaos: kill ──────────────────────────────────────────────────────────
  if (events.has('chaos-kill-random') && chaosState.killedNodeId === nodeId) {
    return makeFailedResult();
  }

  // ── Chaos: network partition ─────────────────────────────────────────────
  if (events.has('chaos-network-partition') && chaosState.partitionGroupA.has(nodeId)) {
    return makeFailedResult();
  }

  // ── Chaos: cascade ───────────────────────────────────────────────────────
  if (events.has('chaos-cascade')) {
    const startTick = chaosState.eventStartTick.get('chaos-cascade') ?? tick;
    if (tick - startTick > 5 && chaosState.killedNodeId !== nodeId) {
      lambda = lambda * 1.5;
    }
  }

  // ── Route to component-specific handler ──────────────────────────────────
  const group = getNodeGroup(defId);

  // Traffic sources
  if (group === 'Clients') {
    return computeSourceMetrics(lambda, config);
  }

  // Load balancer / proxy
  if (defId === 'load-balancer' || defId === 'nginx' || defId === 'haproxy' || defId === 'envoy') {
    return computeLoadBalancerMetrics(lambda, config, events);
  }

  // API Gateway
  if (defId === 'api-gateway') {
    return computeGatewayMetrics(lambda, config, events);
  }

  // Rate limiter
  if (defId === 'rate-limiter') {
    return computeRateLimiterMetrics(lambda, config);
  }

  // WAF / Firewall
  if (defId === 'waf' || defId === 'firewall') {
    return computeWafMetrics(lambda, config, events);
  }

  // CDN / Edge cache
  if (defId === 'cdn' || defId === 'edge-cache') {
    return computeCdnMetrics(lambda, config);
  }

  // Cache nodes
  if (['redis', 'memcached', 'local-cache', 'distributed-cache', 'cache-aside'].includes(defId)) {
    return computeCacheMetrics(lambda, config, events, tick);
  }

  // Database nodes
  if (group === 'Database' || ['postgres', 'mysql', 'mongodb', 'cassandra', 'dynamodb', 'db-replica', 'db-shard', 'db-proxy'].includes(defId)) {
    return computeDatabaseMetrics(lambda, config, events, defId, def, tick);
  }

  // Message queue nodes
  if (['kafka', 'rabbitmq', 'sqs', 'pubsub', 'event-bus', 'nats', 'kinesis'].includes(defId)) {
    return computeQueueMetrics(lambda, config, events, defId, def, tick);
  }

  // Circuit breaker
  if (defId === 'circuit-breaker') {
    return computeCircuitBreakerMetrics(lambda, config);
  }

  // WebSocket server
  if (defId === 'websocket-server') {
    return computeWebSocketMetrics(lambda, config, events, tick);
  }

  // Worker / scheduler / media processor
  if (['worker', 'scheduler', 'media-processor', 'batch-processor'].includes(defId)) {
    return computeWorkerMetrics(lambda, config, events, def, tick);
  }

  // Default: server / microservice
  return computeServerMetrics(lambda, config, events, def, tick);
}

// ── Source / Client ───────────────────────────────────────────────────────────
function computeSourceMetrics(lambda: number, _config: NodeConfig): NodeSimResult {
  const cpu = Math.min(99, 5 + 10 * (lambda / 10000));
  return {
    rps: Math.round(lambda),
    outgoingRps: Math.round(lambda),
    latency: 0,
    p99Latency: 0,
    errorRate: 0,
    cpuLoad: Math.round(cpu * 10) / 10,
    memoryLoad: 30,
    queueDepth: 0,
    status: lambda > 0 ? 'active' : 'idle',
  };
}

// ── Load Balancer ─────────────────────────────────────────────────────────────
function computeLoadBalancerMetrics(
  lambda: number,
  _config: NodeConfig,
  events: Set<ChaosEventId>
): NodeSimResult {
  let mu = 100000;
  const c = 1;
  const rho = Math.min(0.99, lambda / (c * mu));
  const cpu = Math.min(99, rho * 100 * 0.1);
  let latency = 1; // ms constant
  if (events.has('chaos-latency-spike')) latency += 2000;
  if (events.has('chaos-packet-loss')) latency *= 1.43;
  const p99 = latency * 3;
  return {
    rps: Math.round(lambda),
    outgoingRps: Math.round(lambda),
    latency: Math.round(latency * 10) / 10,
    p99Latency: Math.round(p99 * 10) / 10,
    errorRate: events.has('chaos-packet-loss') ? 2.5 : 0,
    cpuLoad: Math.round(cpu * 10) / 10,
    memoryLoad: 20,
    queueDepth: 0,
    status: deriveStatus(rho, events.has('chaos-packet-loss') ? 2.5 : 0),
  };
}

// ── API Gateway ───────────────────────────────────────────────────────────────
function computeGatewayMetrics(
  lambda: number,
  config: NodeConfig,
  events: Set<ChaosEventId>
): NodeSimResult {
  let mu = 10000;
  const maxRps = config.rateLimit ?? Infinity;
  const allowedLambda = Math.min(lambda, maxRps);
  const droppedRps = Math.max(0, lambda - maxRps);
  const rho = Math.min(0.99, allowedLambda / mu);
  const cpu = Math.min(99, rho * 100 * 0.15);
  let latency = 2;
  if (events.has('chaos-latency-spike')) latency += 2000;
  if (events.has('chaos-packet-loss')) latency *= 1.43;
  const p99 = latency * 3;
  let errorRate = (droppedRps / Math.max(1, lambda)) * 100 + (events.has('chaos-packet-loss') ? 2.5 : 0);
  return {
    rps: Math.round(allowedLambda),
    outgoingRps: Math.round(allowedLambda),
    latency: Math.round(latency * 10) / 10,
    p99Latency: Math.round(p99 * 10) / 10,
    errorRate: Math.round(Math.min(100, errorRate) * 100) / 100,
    cpuLoad: Math.round(cpu * 10) / 10,
    memoryLoad: 25,
    queueDepth: 0,
    status: deriveStatus(rho, errorRate),
  };
}

// ── Rate Limiter ──────────────────────────────────────────────────────────────
function computeRateLimiterMetrics(lambda: number, config: NodeConfig): NodeSimResult {
  const rateLimit = config.rateLimit ?? 1000;
  const burstLimit = config.burstLimit ?? rateLimit * 1.5;
  const allowedLambda = Math.min(lambda, rateLimit);
  const droppedRps = Math.max(0, lambda - rateLimit);
  const cpu = Math.min(99, (lambda / burstLimit) * 100 * 0.1);
  let errorRate = (droppedRps / Math.max(1, lambda)) * 100;
  return {
    rps: Math.round(allowedLambda),
    outgoingRps: Math.round(allowedLambda),
    latency: 1,
    p99Latency: 3,
    errorRate: Math.round(Math.min(100, errorRate) * 100) / 100,
    cpuLoad: Math.round(cpu * 10) / 10,
    memoryLoad: 15,
    queueDepth: 0,
    status: errorRate > 10 ? 'degraded' : 'active',
  };
}

// ── WAF / Firewall ────────────────────────────────────────────────────────────
function computeWafMetrics(
  lambda: number,
  _config: NodeConfig,
  events: Set<ChaosEventId>
): NodeSimResult {
  let mu = 50000;
  const rho = Math.min(0.99, lambda / mu);
  const cpu = Math.min(99, rho * 100 * 0.05);
  let latency = 1;
  if (events.has('chaos-latency-spike')) latency += 2000;
  return {
    rps: Math.round(lambda),
    outgoingRps: Math.round(lambda),
    latency: Math.round(latency * 10) / 10,
    p99Latency: Math.round(latency * 3 * 10) / 10,
    errorRate: 0,
    cpuLoad: Math.round(cpu * 10) / 10,
    memoryLoad: 20,
    queueDepth: 0,
    status: deriveStatus(rho, 0),
  };
}

// ── CDN / Edge Cache ──────────────────────────────────────────────────────────
function computeCdnMetrics(lambda: number, config: NodeConfig): NodeSimResult {
  const cacheRatio = (config.cacheRatio ?? 85) / 100;
  const outgoing = Math.round(lambda * (1 - cacheRatio));
  return {
    rps: Math.round(lambda),
    outgoingRps: outgoing,
    latency: 5,
    p99Latency: 15,
    errorRate: 0,
    cpuLoad: 5,
    memoryLoad: 20,
    queueDepth: 0,
    status: lambda > 0 ? 'active' : 'idle',
  };
}

// ── Cache (Redis, Memcached, etc.) ────────────────────────────────────────────
function computeCacheMetrics(
  lambda: number,
  config: NodeConfig,
  events: Set<ChaosEventId>,
  _tick: number
): NodeSimResult {
  const cacheSizeGB = config.cacheSizeGB ?? 4;
  const workingSetSizeGB = config.workingSetSizeGB ?? 20;
  const alpha = config.alpha ?? 1.0;

  const hitRate = cacheHitRateFromWorkingSet(cacheSizeGB, workingSetSizeGB, alpha) / 100;
  const lambdaMiss = lambda * (1 - hitRate);

  // Cache itself: μ = 50000 req/s, c = 1
  let mu = 50000;
  const rho = Math.min(0.99, lambda / mu);
  const cpu = Math.min(99, rho * 100 * 0.2);
  const memoryPct = Math.min(99, Math.max(5, 100 * (cacheSizeGB / (cacheSizeGB + workingSetSizeGB))));

  let latency = 1;
  if (events.has('chaos-latency-spike')) latency += 2000;
  if (events.has('chaos-packet-loss')) latency *= 1.43;

  const wq = mmcQueueDelay(lambda, mu, 1);
  let queueDepth = isFinite(wq) ? Math.round(lambda * wq) : 0;

  return {
    rps: Math.round(lambda),
    outgoingRps: Math.round(lambdaMiss),
    latency: Math.round(latency * 10) / 10,
    p99Latency: Math.round(latency * 3 * 10) / 10,
    errorRate: events.has('chaos-packet-loss') ? 2.5 : 0,
    cpuLoad: Math.round(cpu * 10) / 10,
    memoryLoad: Math.round(memoryPct * 10) / 10,
    queueDepth,
    status: deriveStatus(rho, 0),
  };
}

// ── Server / Microservice ─────────────────────────────────────────────────────
function computeServerMetrics(
  lambda: number,
  config: NodeConfig,
  events: Set<ChaosEventId>,
  def: ComponentDef,
  tick: number
): NodeSimResult {
  // c = instances (default 2), μ = serviceRatePerInstance (default 200)
  const c = Math.max(1, config.instances ?? config.replicas ?? 2);
  let mu = config.serviceRatePerInstance ?? config.scale
    ? (def.defaultThroughput * (config.scale as number ?? 1)) / c
    : def.defaultThroughput / c;
  mu = Math.max(1, mu);

  // Chaos: CPU throttle
  if (events.has('chaos-cpu-throttle')) mu *= 0.10;

  const totalCapacity = c * mu;
  const rho = Math.min(0.99, lambda / totalCapacity);

  const wq = mmcQueueDelay(lambda, mu, c);
  const serviceTime = 1 / mu; // seconds
  const meanSojournSec = isFinite(wq) ? wq + serviceTime : serviceTime * 50;
  const baseLatency = config.latencyOverride ?? def.defaultLatency;
  let meanLatencyMs = baseLatency + meanSojournSec * 1000;

  if (events.has('chaos-latency-spike')) meanLatencyMs += 2000;
  if (events.has('chaos-packet-loss')) meanLatencyMs *= 1.43;

  const p99 = meanLatencyMs * 3;
  const timeoutMs = config.timeoutMs ?? 1000;
  const droppedRps = Math.max(0, lambda - totalCapacity);
  let errorRate = config.failureRate ?? def.defaultErrorRate;
  errorRate += (droppedRps / Math.max(1, lambda)) * 100;
  if (p99 > timeoutMs) {
    errorRate += Math.min(10, (p99 - timeoutMs) / 10);
  }
  if (events.has('chaos-packet-loss')) errorRate += 2.5;

  const cpu = Math.min(99, rho * 100);
  const memory = Math.min(99, 30 + rho * 50);
  let queueDepth = isFinite(wq) ? Math.round(lambda * wq) : Math.round(droppedRps * tick);

  // Memory leak chaos
  let finalCpu = cpu;
  if (events.has('chaos-memory-leak')) {
    const startTick = 0; // approximate
    const elapsed = tick;
    finalCpu = Math.min(99.9, cpu + elapsed * 1.5);
  }

  return {
    rps: Math.round(Math.min(lambda, totalCapacity)),
    outgoingRps: Math.round(Math.min(lambda, totalCapacity)),
    latency: Math.round(meanLatencyMs * 10) / 10,
    p99Latency: Math.round(p99 * 10) / 10,
    errorRate: Math.round(Math.min(100, errorRate) * 100) / 100,
    cpuLoad: Math.round(finalCpu * 10) / 10,
    memoryLoad: Math.round(memory * 10) / 10,
    queueDepth,
    status: deriveStatus(rho, errorRate),
  };
}

// ── WebSocket Server ──────────────────────────────────────────────────────────
function computeWebSocketMetrics(
  lambda: number,
  config: NodeConfig,
  events: Set<ChaosEventId>,
  tick: number
): NodeSimResult {
  const c = Math.max(1, config.instances ?? 2);
  let mu = config.serviceRatePerInstance ?? 500;
  const totalCapacity = c * mu;
  const rho = Math.min(0.99, lambda / totalCapacity);
  const wq = mmcQueueDelay(lambda, mu, c);
  let latency = 2;
  if (events.has('chaos-latency-spike')) latency += 2000;
  const memory = Math.min(99, 40 + rho * 40);
  const cpu = Math.min(99, rho * 100);
  let queueDepth = isFinite(wq) ? Math.round(lambda * wq) : 0;
  return {
    rps: Math.round(Math.min(lambda, totalCapacity)),
    outgoingRps: Math.round(Math.min(lambda, totalCapacity)),
    latency: Math.round(latency * 10) / 10,
    p99Latency: Math.round(latency * 3 * 10) / 10,
    errorRate: 0,
    cpuLoad: Math.round(cpu * 10) / 10,
    memoryLoad: Math.round(memory * 10) / 10,
    queueDepth,
    status: deriveStatus(rho, 0),
  };
}

// ── Database ──────────────────────────────────────────────────────────────────
function computeDatabaseMetrics(
  lambda: number,
  config: NodeConfig,
  events: Set<ChaosEventId>,
  defId: string,
  def: ComponentDef,
  _tick: number
): NodeSimResult {
  const instanceSizeMap: Record<string, number> = {
    small: 1000, medium: 2000, large: 4000, xlarge: 8000,
  };
  const muPerNode = instanceSizeMap[config.instanceSize ?? 'medium'];
  const readReplicas = config.readReplicas ?? 0;
  const shards = config.shards ?? 1;
  const c = Math.max(1, shards + readReplicas + 1);
  let mu = muPerNode;

  if (events.has('chaos-db-slow')) mu *= 0.10;

  const totalCapacity = c * mu;
  const rho = Math.min(0.99, lambda / totalCapacity);
  const wq = mmcQueueDelay(lambda, mu, c);
  const serviceTime = 1 / mu;
  const meanSojournSec = isFinite(wq) ? wq + serviceTime : serviceTime * 50;
  const baseLatency = config.latencyOverride ?? def.defaultLatency;
  let meanLatencyMs = baseLatency + meanSojournSec * 1000 + 5; // 5ms base DB overhead

  if (events.has('chaos-latency-spike')) meanLatencyMs += 2000;
  if (events.has('chaos-packet-loss')) meanLatencyMs *= 1.43;

  const p99 = meanLatencyMs * 3;
  const droppedRps = Math.max(0, lambda - totalCapacity);
  let errorRate = config.failureRate ?? def.defaultErrorRate;
  errorRate += (droppedRps / Math.max(1, lambda)) * 100;
  if (events.has('chaos-packet-loss')) errorRate += 2.5;

  const cpu = Math.min(99, rho * 100);
  const memory = Math.min(99, 40 + rho * 45);
  let queueDepth = isFinite(wq) ? Math.round(lambda * wq) : 0;

  return {
    rps: Math.round(Math.min(lambda, totalCapacity)),
    outgoingRps: Math.round(Math.min(lambda, totalCapacity)),
    latency: Math.round(meanLatencyMs * 10) / 10,
    p99Latency: Math.round(p99 * 10) / 10,
    errorRate: Math.round(Math.min(100, errorRate) * 100) / 100,
    cpuLoad: Math.round(cpu * 10) / 10,
    memoryLoad: Math.round(memory * 10) / 10,
    queueDepth,
    status: deriveStatus(rho, errorRate),
  };
}

// ── Message Queue ─────────────────────────────────────────────────────────────
function computeQueueMetrics(
  lambda: number,
  config: NodeConfig,
  events: Set<ChaosEventId>,
  _defId: string,
  def: ComponentDef,
  _tick: number
): NodeSimResult {
  const partitions = config.partitions ?? 8;
  const muPerPartition = 10000; // req/s per partition
  const totalCapacity = partitions * muPerPartition;
  const consumerCapacity = config.consumerCapacity ?? totalCapacity * 0.8;

  const rho = Math.min(0.99, lambda / totalCapacity);
  const wq = mmcQueueDelay(lambda, muPerPartition, partitions);
  const wqMs = isFinite(wq) ? wq * 1000 : 9999;
  let latency = 3 + wqMs;
  if (events.has('chaos-latency-spike')) latency += 2000;

  const cpu = Math.min(99, rho * 100 * 0.1);
  const memory = Math.min(99, 30 + rho * 40);
  let queueDepth = isFinite(wq) ? Math.round(lambda * wq) : Math.round(lambda * 10);
  const outgoing = Math.round(Math.min(lambda, consumerCapacity));

  return {
    rps: Math.round(Math.min(lambda, totalCapacity)),
    outgoingRps: outgoing,
    latency: Math.round(latency * 10) / 10,
    p99Latency: Math.round(latency * 3 * 10) / 10,
    errorRate: events.has('chaos-packet-loss') ? 2.5 : 0,
    cpuLoad: Math.round(cpu * 10) / 10,
    memoryLoad: Math.round(memory * 10) / 10,
    queueDepth,
    status: deriveStatus(rho, 0),
  };
}

// ── Circuit Breaker ───────────────────────────────────────────────────────────
function computeCircuitBreakerMetrics(lambda: number, config: NodeConfig): NodeSimResult {
  const state = config.circuitState ?? 'closed';
  if (state === 'open') {
    return {
      rps: 0, outgoingRps: 0, latency: 1, p99Latency: 3,
      errorRate: 100, cpuLoad: 1, memoryLoad: 10, queueDepth: 0, status: 'failed',
    };
  }
  if (state === 'half-open') {
    return {
      rps: Math.round(lambda * 0.1), outgoingRps: Math.round(lambda * 0.1),
      latency: 1, p99Latency: 3, errorRate: 90, cpuLoad: 1, memoryLoad: 10,
      queueDepth: 0, status: 'degraded',
    };
  }
  // closed
  return {
    rps: Math.round(lambda), outgoingRps: Math.round(lambda),
    latency: 1, p99Latency: 3, errorRate: 0, cpuLoad: 1, memoryLoad: 10,
    queueDepth: 0, status: lambda > 0 ? 'active' : 'idle',
  };
}

// ── Worker / Scheduler ────────────────────────────────────────────────────────
function computeWorkerMetrics(
  lambda: number,
  config: NodeConfig,
  events: Set<ChaosEventId>,
  def: ComponentDef,
  tick: number
): NodeSimResult {
  return computeServerMetrics(lambda, config, events, def, tick);
}

// ── Status derivation ─────────────────────────────────────────────────────────
function deriveStatus(rho: number, errorRate: number): CanvasNode['status'] {
  if (rho >= 0.99 || errorRate > 10) return 'failed';
  if (rho >= 0.75 || errorRate > 2) return 'degraded';
  if (rho > 0) return 'active';
  return 'idle';
}

function makeFailedResult(): NodeSimResult {
  return {
    rps: 0, outgoingRps: 0, latency: 0, p99Latency: 0,
    errorRate: 100, cpuLoad: 0, memoryLoad: 0, queueDepth: 0, status: 'failed',
  };
}

// ─── Legacy computeNodeMetrics (backward-compatible wrapper) ──────────────────

/**
 * @deprecated Use computeNodeRuntime + two-pass propagation instead.
 * Kept for backward compatibility with ArchitectureCanvasScreen.tsx.
 */
export interface LegacyNodeSimResult {
  rps: number;
  latency: number;
  errorRate: number;
  cpuLoad: number;
  status: CanvasNode['status'];
}

export function computeNodeMetrics(
  def: ComponentDef,
  arrivalRate: number,
  chaosState: ChaosState,
  nodeId: string,
  tick: number,
  nodeConfig?: Record<string, string | number | boolean>
): LegacyNodeSimResult {
  const config: NodeConfig = nodeConfig ?? {};
  const result = computeNodeRuntime(def.id, def, arrivalRate, config, chaosState, nodeId, tick);
  return {
    rps: result.rps,
    latency: result.latency,
    errorRate: result.errorRate,
    cpuLoad: result.cpuLoad,
    status: result.status,
  };
}

// ─── Two-Pass Traffic Propagation ─────────────────────────────────────────────

/**
 * Compute topological order of nodes (Kahn's algorithm).
 * Returns nodes in processing order; cycles are handled by ignoring back-edges.
 */
function topologicalOrder(nodes: CanvasNode[], edges: CanvasEdge[]): CanvasNode[] {
  const nodeMap = new Map(nodes.map(n => [n.id, n]));
  const inDegree = new Map<string, number>();
  const adj = new Map<string, string[]>();

  for (const n of nodes) {
    inDegree.set(n.id, 0);
    adj.set(n.id, []);
  }
  for (const e of edges) {
    if (nodeMap.has(e.sourceId) && nodeMap.has(e.targetId)) {
      adj.get(e.sourceId)!.push(e.targetId);
      inDegree.set(e.targetId, (inDegree.get(e.targetId) ?? 0) + 1);
    }
  }

  const queue: string[] = [];
  for (const [id, deg] of inDegree) {
    if (deg === 0) queue.push(id);
  }

  const result: CanvasNode[] = [];
  const visited = new Set<string>();
  while (queue.length > 0) {
    const id = queue.shift()!;
    if (visited.has(id)) continue;
    visited.add(id);
    const node = nodeMap.get(id);
    if (node) result.push(node);
    for (const next of (adj.get(id) ?? [])) {
      const newDeg = (inDegree.get(next) ?? 1) - 1;
      inDegree.set(next, newDeg);
      if (newDeg === 0) queue.push(next);
    }
  }

  // Add any remaining nodes (cycles) at the end
  for (const n of nodes) {
    if (!visited.has(n.id)) result.push(n);
  }

  return result;
}

/**
 * Two-pass traffic propagation:
 * Pass 1: Forward propagate RPS through the graph in topological order,
 *         using each node's outgoing RPS (which respects filtering/dropping).
 * Pass 2: Compute full metrics for each node using its final input RPS.
 *
 * Returns updated nodes with metrics and a map of edge RPS values.
 */
export function propagateTraffic(
  nodes: CanvasNode[],
  edges: CanvasEdge[],
  chaosState: ChaosState,
  tick: number
): { updatedNodes: CanvasNode[]; edgeRpsMap: Map<string, number> } {
  if (nodes.length === 0) {
    return { updatedNodes: nodes, edgeRpsMap: new Map() };
  }

  const nodeMap = new Map(nodes.map(n => [n.id, n]));
  const adj = new Map<string, string[]>();
  const edgeMap = new Map<string, CanvasEdge[]>(); // sourceId → edges

  for (const n of nodes) {
    adj.set(n.id, []);
    edgeMap.set(n.id, []);
  }
  for (const e of edges) {
    if (nodeMap.has(e.sourceId) && nodeMap.has(e.targetId)) {
      adj.get(e.sourceId)!.push(e.targetId);
      edgeMap.get(e.sourceId)!.push(e);
    }
  }

  // ── Pass 1: Forward propagation ───────────────────────────────────────────
  const nodeInputRps = new Map<string, number>();
  const nodeOutputRps = new Map<string, number>();
  const edgeRpsMap = new Map<string, number>();

  // Initialize all nodes to 0 input
  for (const n of nodes) nodeInputRps.set(n.id, 0);

  // Source nodes get their clientRps as input
  for (const n of nodes) {
    const def = ALL_COMPONENTS.find(c => c.id === n.defId);
    if (def?.group === 'Clients') {
      const hasOutgoing = edges.some(e => e.sourceId === n.id);
      const clientRps = hasOutgoing ? ((n.config.clientRps as number) ?? 1000) : 0;
      nodeInputRps.set(n.id, clientRps);
    }
  }

  // Process in topological order
  const ordered = topologicalOrder(nodes, edges);

  for (const node of ordered) {
    const def = ALL_COMPONENTS.find(c => c.id === node.defId);
    if (!def) continue;

    let lambda = nodeInputRps.get(node.id) ?? 0;

    // Compute outgoing RPS using component-specific model (quick pass for routing)
    const result = computeNodeRuntime(node.defId, def, lambda, node.config as NodeConfig, chaosState, node.id, tick);
    const outgoing = result.outgoingRps;
    nodeOutputRps.set(node.id, outgoing);

    // Distribute outgoing RPS to successors
    const outEdges = edgeMap.get(node.id) ?? [];
    if (outEdges.length > 0) {
      const rpsPerEdge = outgoing / outEdges.length;
      for (const edge of outEdges) {
        edgeRpsMap.set(edge.id, Math.round(rpsPerEdge));
        const current = nodeInputRps.get(edge.targetId) ?? 0;
        nodeInputRps.set(edge.targetId, current + rpsPerEdge);
      }
    }
  }

  // ── Pass 2: Compute full metrics ──────────────────────────────────────────
  const updatedNodes: CanvasNode[] = nodes.map(node => {
    const def = ALL_COMPONENTS.find(c => c.id === node.defId);
    if (!def) return node;

    let lambda = nodeInputRps.get(node.id) ?? 0;
    const result = computeNodeRuntime(node.defId, def, lambda, node.config as NodeConfig, chaosState, node.id, tick);

    return {
      ...node,
      status: result.status,
      metrics: {
        rps: result.rps,
        latency: result.latency,
        errorRate: result.errorRate,
        cpuLoad: result.cpuLoad,
      },
    };
  });

  return { updatedNodes, edgeRpsMap };
}

// ─── System-level metrics computation ────────────────────────────────────────

export interface SystemSimResult {
  rps: number;
  p99: number;
  errorRate: number;
  availability: number;
  cacheHit: number;
  dbLoad: number;
  queueDepth: number;
}

export function computeSystemMetrics(
  nodes: CanvasNode[],
  edges: CanvasEdge[],
  targetRps: number,
  tick: number,
  chaosState: ChaosState
): SystemSimResult {
  if (targetRps === 0 || nodes.length === 0) {
    const availability = nodes.length > 0 ? computeSystemAvailability(nodes, edges) * 100 : 100;
    return {
      rps: 0, p99: 0, errorRate: 0,
      availability: Math.round(availability * 10000) / 10000,
      cacheHit: 0, dbLoad: 0, queueDepth: 0,
    };
  }

  const activeNodes = nodes.filter(n => n.status !== 'failed');

  // ── System RPS: sum of RPS at sink nodes (no outgoing edges) ─────────────
  const sinkNodes = activeNodes.filter(n => !edges.some(e => e.sourceId === n.id));
  const systemRps = sinkNodes.length > 0
    ? sinkNodes.reduce((sum, n) => sum + n.metrics.rps, 0)
    : activeNodes.reduce((max, n) => Math.max(max, n.metrics.rps), 0);

  // ── P99: critical path latency × 3 ───────────────────────────────────────
  const criticalPathLatency = computeCriticalPathLatency(nodes, edges);
  const p99 = criticalPathLatency * 3;

  // ── Error rate: series product along main path ────────────────────────────
  const pathNodes = getMainPathNodes(nodes, edges);
  let systemErrorRate = 0;
  if (pathNodes.length > 0) {
    const successProb = pathNodes.reduce((prod, n) => prod * (1 - n.metrics.errorRate / 100), 1);
    systemErrorRate = (1 - successProb) * 100;
  }

  // ── Availability ──────────────────────────────────────────────────────────
  const availability = computeSystemAvailability(nodes, edges) * 100;

  // ── Cache hit rate: from cache node configs ───────────────────────────────
  const cacheNodes = activeNodes.filter(n =>
    ['redis', 'memcached', 'local-cache', 'distributed-cache', 'cache-aside', 'cdn', 'edge-cache'].includes(n.defId)
  );
  let cacheHit = 0;
  if (cacheNodes.length > 0) {
    const hitRates = cacheNodes.map(n => {
      const config = n.config as NodeConfig;
      if (n.defId === 'cdn' || n.defId === 'edge-cache') {
        return config.cacheRatio ?? 85;
      }
      return cacheHitRateFromWorkingSet(
        config.cacheSizeGB ?? 4,
        config.workingSetSizeGB ?? 20,
        config.alpha ?? 1.0
      );
    });
    cacheHit = hitRates.reduce((sum, r) => sum + r, 0) / hitRates.length;
  }

  // ── DB load ───────────────────────────────────────────────────────────────
  const dbNodes = activeNodes.filter(n =>
    ALL_COMPONENTS.find(c => c.id === n.defId)?.group === 'Database'
  );
  let dbLoad = 0;
  if (dbNodes.length > 0) {
    const avgDbCpu = dbNodes.reduce((sum, n) => sum + n.metrics.cpuLoad, 0) / dbNodes.length;
    dbLoad = Math.round(avgDbCpu * 10) / 10;
  }

  // ── Queue depth: max across all messaging nodes ───────────────────────────
  const messagingNodes = activeNodes.filter(n =>
    ['kafka', 'rabbitmq', 'sqs', 'pubsub', 'event-bus', 'nats', 'kinesis'].includes(n.defId)
  );
  let queueDepth = 0;
  if (messagingNodes.length > 0) {
    // Re-compute queue depth using the node's current RPS and config
    for (const n of messagingNodes) {
      const config = n.config as NodeConfig;
      const partitions = config.partitions ?? 8;
      const muPerPartition = 10000;
      let lambda = n.metrics.rps;
      const wq = mmcQueueDelay(lambda, muPerPartition, partitions);
      const depth = isFinite(wq) ? Math.round(lambda * wq) : Math.round(lambda * tick);
      queueDepth = Math.max(queueDepth, depth);
    }
  }

  return {
    rps: Math.round(Math.min(systemRps, targetRps)),
    p99: Math.round(p99 * 10) / 10,
    errorRate: Math.round(systemErrorRate * 100) / 100,
    availability: Math.round(availability * 10000) / 10000,
    cacheHit: Math.round(cacheHit * 10) / 10,
    dbLoad: Math.round(Math.min(100, dbLoad) * 10) / 10,
    queueDepth: Math.max(0, queueDepth),
  };
}

// ─── Graph traversal helpers ──────────────────────────────────────────────────

function computeCriticalPathLatency(nodes: CanvasNode[], edges: CanvasEdge[]): number {
  if (nodes.length === 0) return 0;

  const adj = new Map<string, string[]>();
  for (const node of nodes) adj.set(node.id, []);
  for (const edge of edges) {
    if (adj.has(edge.sourceId)) adj.get(edge.sourceId)!.push(edge.targetId);
  }

  const hasIncoming = new Set(edges.map(e => e.targetId));
  const sources = nodes.filter(n => !hasIncoming.has(n.id));
  if (sources.length === 0) {
    return nodes.reduce((sum, n) => sum + n.metrics.latency, 0);
  }

  const nodeMap = new Map(nodes.map(n => [n.id, n]));
  let maxLatency = 0;

  function dfs(nodeId: string, accLatency: number, visited: Set<string>) {
    if (visited.has(nodeId)) return;
    visited.add(nodeId);
    const node = nodeMap.get(nodeId);
    if (!node) return;
    const newLatency = accLatency + node.metrics.latency;
    maxLatency = Math.max(maxLatency, newLatency);
    for (const child of (adj.get(nodeId) ?? [])) {
      dfs(child, newLatency, new Set(visited));
    }
  }

  for (const src of sources) dfs(src.id, 0, new Set());
  return maxLatency;
}

function getMainPathNodes(nodes: CanvasNode[], edges: CanvasEdge[]): CanvasNode[] {
  if (nodes.length === 0) return [];

  const adj = new Map<string, string[]>();
  for (const node of nodes) adj.set(node.id, []);
  for (const edge of edges) {
    if (adj.has(edge.sourceId)) adj.get(edge.sourceId)!.push(edge.targetId);
  }

  const hasIncoming = new Set(edges.map(e => e.targetId));
  const sources = nodes.filter(n => !hasIncoming.has(n.id));
  if (sources.length === 0) return nodes;

  const nodeMap = new Map(nodes.map(n => [n.id, n]));
  let longestPath: CanvasNode[] = [];

  function dfs(nodeId: string, path: CanvasNode[], visited: Set<string>) {
    if (visited.has(nodeId)) return;
    visited.add(nodeId);
    const node = nodeMap.get(nodeId);
    if (!node) return;
    const newPath = [...path, node];
    if (newPath.length > longestPath.length) longestPath = newPath;
    for (const child of (adj.get(nodeId) ?? [])) {
      dfs(child, newPath, new Set(visited));
    }
  }

  for (const src of sources) dfs(src.id, [], new Set());
  return longestPath;
}

// ─── Chaos event management ───────────────────────────────────────────────────

export function toggleChaosEvent(
  chaosState: ChaosState,
  eventId: ChaosEventId,
  nodes: CanvasNode[],
  tick: number
): ChaosState {
  const newState: ChaosState = {
    activeEvents: new Set(chaosState.activeEvents),
    eventStartTick: new Map(chaosState.eventStartTick),
    killedNodeId: chaosState.killedNodeId,
    partitionGroupA: new Set(chaosState.partitionGroupA),
  };

  if (newState.activeEvents.has(eventId)) {
    newState.activeEvents.delete(eventId);
    newState.eventStartTick.delete(eventId);
    if (eventId === 'chaos-kill-random' || eventId === 'chaos-cascade') {
      newState.killedNodeId = null;
    }
    if (eventId === 'chaos-network-partition') {
      newState.partitionGroupA.clear();
    }
  } else {
    newState.activeEvents.add(eventId);
    newState.eventStartTick.set(eventId, tick);

    if (eventId === 'chaos-kill-random' || eventId === 'chaos-cascade') {
      const killable = nodes.filter(n => {
        const def = ALL_COMPONENTS.find(c => c.id === n.defId);
        return def && ['Compute', 'Database', 'Cache', 'Messaging'].includes(def.group);
      });
      if (killable.length > 0) {
        const idx = Math.floor(Math.abs(Math.sin(tick * 7.3)) * killable.length);
        newState.killedNodeId = killable[Math.min(idx, killable.length - 1)].id;
      }
    }

    if (eventId === 'chaos-network-partition') {
      const half = Math.floor(nodes.length / 2);
      for (let i = 0; i < half; i++) {
        newState.partitionGroupA.add(nodes[i].id);
      }
    }
  }

  return newState;
}

export function expireChaosEvents(chaosState: ChaosState, tick: number): ChaosState {
  let changed = false;
  const newState: ChaosState = {
    activeEvents: new Set(chaosState.activeEvents),
    eventStartTick: new Map(chaosState.eventStartTick),
    killedNodeId: chaosState.killedNodeId,
    partitionGroupA: new Set(chaosState.partitionGroupA),
  };

  for (const [eventId, startTick] of chaosState.eventStartTick.entries()) {
    const duration = CHAOS_DURATIONS[eventId as ChaosEventId] ?? 30;
    if (tick - startTick >= duration) {
      newState.activeEvents.delete(eventId as ChaosEventId);
      newState.eventStartTick.delete(eventId);
      if (eventId === 'chaos-kill-random' || eventId === 'chaos-cascade') {
        newState.killedNodeId = null;
      }
      if (eventId === 'chaos-network-partition') {
        newState.partitionGroupA.clear();
      }
      changed = true;
    }
  }

  return changed ? newState : chaosState;
}

// ─── Dynamic trace generation ─────────────────────────────────────────────────

export interface TraceSpan {
  service: string;
  duration: number;
  status: 'ok' | 'slow' | 'error';
}

export interface TraceEntry {
  traceId: string;
  path: string;
  duration: number;
  status: 'ok' | 'slow' | 'error';
  spans: number;
  spanDetails: TraceSpan[];
}

export function generateDynamicTraces(
  nodes: CanvasNode[],
  edges: CanvasEdge[],
  tick: number
): TraceEntry[] {
  if (nodes.length === 0) return [];

  const nodeMap = new Map(nodes.map(n => [n.id, n]));
  const adj = new Map<string, string[]>();
  for (const node of nodes) adj.set(node.id, []);
  for (const edge of edges) {
    if (adj.has(edge.sourceId)) adj.get(edge.sourceId)!.push(edge.targetId);
  }

  const hasIncoming = new Set(edges.map(e => e.targetId));
  const sources = nodes.filter(n => !hasIncoming.has(n.id));

  const allPaths: CanvasNode[][] = [];

  function findPaths(nodeId: string, path: CanvasNode[], visited: Set<string>) {
    if (visited.has(nodeId) || path.length > 8) return;
    const node = nodeMap.get(nodeId);
    if (!node) return;
    const newPath = [...path, node];
    visited.add(nodeId);
    const children = adj.get(nodeId) ?? [];
    if (children.length === 0 || newPath.length >= 8) {
      allPaths.push(newPath);
    } else {
      for (const child of children) {
        findPaths(child, newPath, new Set(visited));
      }
    }
  }

  const startNodes = sources.length > 0 ? sources : nodes.slice(0, 1);
  for (const src of startNodes) findPaths(src.id, [], new Set());

  const uniquePaths = allPaths.filter(p => p.length >= 2).slice(0, 6);

  if (uniquePaths.length === 0) {
    return nodes.slice(0, 5).map((node, i) => {
      const def = ALL_COMPONENTS.find(c => c.id === node.defId);
      const lat = node.metrics.latency || (def?.defaultLatency ?? 10);
      const status: TraceEntry['status'] = node.metrics.errorRate > 2 ? 'error' : lat > 100 ? 'slow' : 'ok';
      return {
        traceId: `trc-${generateTraceId(node.id, tick + i)}`,
        path: node.label,
        duration: Math.round(lat),
        status,
        spans: 1,
        spanDetails: [{ service: node.label, duration: Math.round(lat), status }],
      };
    });
  }

  return uniquePaths.map((path, i) => {
    const spanDetails: TraceSpan[] = path.map(node => {
      const lat = node.metrics.latency || (ALL_COMPONENTS.find(c => c.id === node.defId)?.defaultLatency ?? 10);
      const spanStatus: TraceSpan['status'] =
        node.metrics.errorRate > 2 ? 'error' : lat > 200 ? 'slow' : 'ok';
      return { service: node.label, duration: Math.round(lat), status: spanStatus };
    });

    const totalDuration = spanDetails.reduce((s, sp) => s + sp.duration, 0);
    const hasError = spanDetails.some(sp => sp.status === 'error');
    const hasSlow = spanDetails.some(sp => sp.status === 'slow');
    const traceStatus: TraceEntry['status'] = hasError ? 'error' : hasSlow ? 'slow' : 'ok';

    return {
      traceId: `trc-${generateTraceId(path.map(n => n.id).join(''), tick + i)}`,
      path: path.map(n => n.label).join(' → '),
      duration: totalDuration,
      status: traceStatus,
      spans: path.length,
      spanDetails,
    };
  });
}

function generateTraceId(seed: string, tick: number): string {
  let h = 0;
  const str = seed + tick;
  for (let i = 0; i < str.length; i++) {
    h = (Math.imul(31, h) + str.charCodeAt(i)) | 0;
  }
  return Math.abs(h).toString(16).slice(0, 6).padStart(6, '0');
}
